import React, { useState, useEffect } from 'react';
import { api } from '../api/client';
import { useCurrentUser } from '../contexts/CurrentUserContext';
import { invalidateWorkspaceRoster } from '../hooks/useWorkspaceRoster';
import './TeamManagement.css';

function generateUserColor() {
  const hues = [220, 160, 280, 30, 0, 200, 120, 310];
  return `hsl(${hues[Math.floor(Math.random() * hues.length)]}, 60%, 55%)`;
}

function fmtCurrency(amount, currency = 'GBP') {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency', currency,
    minimumFractionDigits: 0, maximumFractionDigits: 2,
  }).format(amount);
}

const DEPARTMENTS = [
  '', 'Admin', 'Design', 'Engineering', 'Finance', 'HR',
  'IT', 'Legal', 'Management', 'Marketing', 'Operations',
  'PR', 'Product', 'Sales', 'Other',
];

const DEPT_COLORS = {
  Admin: '#6b7280', Design: '#8b5cf6', Engineering: '#3b82f6',
  Finance: '#10b981', HR: '#f59e0b', IT: '#0ea5e9',
  Legal: '#64748b', Management: '#0c2d48', Marketing: '#ec4899',
  Operations: '#f97316', PR: '#a855f7', Product: '#14b8a6',
  Sales: '#22c55e', Other: '#9ca3af',
};

const EMPTY_USER = { name: '', email: '', role: 'user', color: '', department: '', jobTitle: '' };

// Returns black or white text depending on background luminance
function getTextColor(hex) {
  if (!hex) return '#fff';
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.55 ? '#1a1a1a' : '#fff';
}

// Shared form fields — defined OUTSIDE TeamManagement so React sees a stable component reference
function UserFormFields({ data, onChange, allKnownDepts }) {
  // customMode = user explicitly clicked "Type your own…"
  // Initialise true only when the dept value isn't in the known list (e.g. old data)
  const isUnknownDept = !!(data.department && !allKnownDepts.includes(data.department));
  const [customMode, setCustomMode] = React.useState(isUnknownDept);

  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
        <div className="form-group">
          <label>Full name *</label>
          <input type="text" value={data.name} onChange={e => onChange({ ...data, name: e.target.value })} required />
        </div>
        <div className="form-group">
          <label>Email *</label>
          <input type="email" value={data.email} onChange={e => onChange({ ...data, email: e.target.value })} required />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
        <div className="form-group">
          <label>Department</label>
          <select
            value={customMode ? '__custom__' : (data.department || '')}
            onChange={e => {
              if (e.target.value === '__custom__') {
                setCustomMode(true);
                onChange({ ...data, department: '' });
              } else {
                setCustomMode(false);
                onChange({ ...data, department: e.target.value });
              }
            }}
          >
            {allKnownDepts.map(d => <option key={d} value={d}>{d || '— None —'}</option>)}
            <option value="__custom__">Type your own…</option>
          </select>
          {customMode && (
            <input
              type="text"
              value={data.department || ''}
              onChange={e => onChange({ ...data, department: e.target.value })}
              placeholder="Custom department name"
              style={{ marginTop: '0.4rem', width: '100%', padding: '0.4rem 0.6rem', borderRadius: 6, border: '1px solid #c5cdd4', fontSize: '0.9rem', boxSizing: 'border-box', fontFamily: 'Raleway, sans-serif' }}
            />
          )}
        </div>
        <div className="form-group">
          <label>Job title</label>
          <input type="text" value={data.jobTitle || ''} onChange={e => onChange({ ...data, jobTitle: e.target.value })} placeholder="e.g. Senior Developer" />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
        <div className="form-group">
          <label>System role</label>
          <select value={data.role} onChange={e => onChange({ ...data, role: e.target.value })}>
            <option value="user">User</option>
            <option value="admin">Admin</option>
          </select>
        </div>
        <div className="form-group">
          <label>Avatar colour</label>
          <input type="color" value={data.color || '#3498db'} onChange={e => onChange({ ...data, color: e.target.value })} />
        </div>
      </div>
    </>
  );
}

const TeamManagement = () => {
  const { currentUser, isAdmin } = useCurrentUser();
  const [users, setUsers] = useState([]);
  const [teamTasks, setTeamTasks] = useState([]);
  const [isAddingUser, setIsAddingUser] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [deptFilter, setDeptFilter] = useState('');

  const [showRemovalModal, setShowRemovalModal] = useState(false);
  const [userToRemove, setUserToRemove] = useState(null);
  const [removeLoading, setRemoveLoading] = useState(false);
  const [newUser, setNewUser] = useState(EMPTY_USER);
  const [seatInfo, setSeatInfo] = useState(null);
  const [seatConfirm, setSeatConfirm] = useState(null);
  const [addLoading, setAddLoading] = useState(false);

  // Persistent custom departments
  const [customDepts, setCustomDepts] = useState(() => {
    try { return JSON.parse(localStorage.getItem('teamCustomDepts') || '[]'); } catch { return []; }
  });
  // Custom dept colours stored separately so they survive the built-in list too
  const [deptColorOverrides, setDeptColorOverrides] = useState(() => {
    try { return JSON.parse(localStorage.getItem('teamDeptColors') || '{}'); } catch { return {}; }
  });

  // Add department inline form state
  const [showAddDeptForm, setShowAddDeptForm] = useState(false);
  const [newDeptName, setNewDeptName]   = useState('');
  const [newDeptColor, setNewDeptColor] = useState('');

  // Edit department state
  const [editingDept, setEditingDept] = useState(null); // { originalName, name, color }

  const PRESET_COLORS = [
    '#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b',
    '#10b981', '#f97316', '#0ea5e9', '#6b7280',
  ];

  function saveDeptColorOverride(dept, color) {
    const updated = { ...deptColorOverrides, [dept]: color };
    setDeptColorOverrides(updated);
    localStorage.setItem('teamDeptColors', JSON.stringify(updated));
  }

  function addCustomDept() {
    const d = newDeptName.trim();
    if (!d || !newDeptColor) return;
    if (!DEPARTMENTS.includes(d) && !customDepts.includes(d)) {
      const updated = [...customDepts, d];
      setCustomDepts(updated);
      localStorage.setItem('teamCustomDepts', JSON.stringify(updated));
    }
    saveDeptColorOverride(d, newDeptColor);
    setNewDeptName('');
    setNewDeptColor('');
    setShowAddDeptForm(false);
  }

  function removeCustomDept(dept) {
    const updated = customDepts.filter(d => d !== dept);
    setCustomDepts(updated);
    localStorage.setItem('teamCustomDepts', JSON.stringify(updated));
    if (deptFilter === dept) setDeptFilter('');
  }

  function openEditDept(dept) {
    setEditingDept({ originalName: dept, name: dept, color: getDeptColor(dept) });
    setShowAddDeptForm(false);
  }

  function saveEditDept() {
    if (!editingDept) return;
    const { originalName, name: rawName, color } = editingDept;
    const newName = rawName.trim();
    if (!newName || !color) return;

    // Persist colour override under new name
    const newOverrides = { ...deptColorOverrides };
    if (originalName !== newName) delete newOverrides[originalName];
    newOverrides[newName] = color;
    setDeptColorOverrides(newOverrides);
    localStorage.setItem('teamDeptColors', JSON.stringify(newOverrides));

    // Keep customDepts list consistent
    if (originalName !== newName) {
      let updatedCustom = [...customDepts];
      if (updatedCustom.includes(originalName)) {
        updatedCustom = updatedCustom.map(d => d === originalName ? newName : d);
      } else if (!DEPARTMENTS.includes(newName) && !updatedCustom.includes(newName)) {
        updatedCustom.push(newName);
      }
      setCustomDepts(updatedCustom);
      localStorage.setItem('teamCustomDepts', JSON.stringify(updatedCustom));

      reload();
      if (deptFilter === originalName) setDeptFilter(newName);
    }

    setEditingDept(null);
  }

  // Colour resolver — overrides take priority over DEPT_COLORS defaults
  function getDeptColor(dept) {
    return deptColorOverrides[dept] || DEPT_COLORS[dept] || '#9ca3af';
  }

  async function reload() {
    try {
      const res = await api('/api/team/members');
      const data = res && res.ok ? await res.json() : null;
      if (data?.members) {
        setUsers(data.members.map(m => ({
          ...m,
          role: m.teamRole || m.role,
        })));
      }
    } catch (_) {}

    // Per-member open-task counts need a server aggregate — the client only holds
    // the current user's tasks. Use the business-scoped team endpoint (Block 2).
    try {
      const res = await api('/api/tasks?team=true');
      const data = res && res.ok ? await res.json() : null;
      setTeamTasks(Array.isArray(data?.tasks) ? data.tasks : []);
    } catch (_) { setTeamTasks([]); }
  }

  async function loadSeatInfo() {
    try {
      const res = await api('/api/team/seat-info');
      if (res?.ok) setSeatInfo(await res.json());
    } catch (_) {}
  }

  useEffect(() => { reload(); loadSeatInfo(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const allDepts = [...new Set([...users.map(u => u.department).filter(Boolean), ...customDepts])].sort();

  const filtered = users.filter(u => {
    const q = searchQuery.toLowerCase();
    const matchesSearch = !q || u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q) ||
      (u.jobTitle || '').toLowerCase().includes(q) || (u.department || '').toLowerCase().includes(q);
    const matchesDept = !deptFilter || u.department === deptFilter;
    return matchesSearch && matchesDept;
  });

  const handleAddUser = (e) => {
    e.preventDefault();
    if (!newUser.name.trim() || !newUser.email.trim()) return;
    if (seatInfo && seatInfo.paidSeats !== null && seatInfo.usedSeats >= seatInfo.paidSeats) {
      setSeatConfirm({ userData: newUser });
      return;
    }
    submitAddMember(newUser, false);
  };

  const submitAddMember = async (userData, addSeat) => {
    setAddLoading(true);
    try {
      await api('/api/team/members/add', {
        method: 'POST',
        body: JSON.stringify({
          ...userData,
          color: userData.color || generateUserColor(),
          addSeat,
        }),
      });
      invalidateWorkspaceRoster();
      await reload();
      await loadSeatInfo();
      setIsAddingUser(false);
      setNewUser(EMPTY_USER);
      setSeatConfirm(null);
    } catch (err) {
      alert(err.message || 'Failed to add member');
      setSeatConfirm(null);
    } finally {
      setAddLoading(false);
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      await api(`/api/team/members/${editingUser.id}/profile`, {
        method: 'PATCH',
        body: JSON.stringify({
          name: editingUser.name,
          department: editingUser.department,
          jobTitle: editingUser.jobTitle,
          color: editingUser.color,
          role: editingUser.role,
        }),
      });
      await reload();
      setEditingUser(null);
    } catch (err) { alert(err.message || 'Failed to update'); }
  };

  const handleDeleteUser = (userId) => {
    const target = users.find(u => u.id === userId);
    if (!target) return;
    if (userId === currentUser.id) { alert('You cannot remove yourself!'); return; }
    const admins = users.filter(u => u.role === 'admin' || u.role === 'super_admin');
    if ((target.role === 'admin' || target.role === 'super_admin') && admins.length <= 1) {
      alert('Cannot remove the last admin.'); return;
    }
    setUserToRemove(target);
    setShowRemovalModal(true);
  };

  const handleConfirmRemoval = async () => {
    setRemoveLoading(true);
    try {
      await api(`/api/team/members/${userToRemove.id}`, { method: 'DELETE' });
      invalidateWorkspaceRoster();
      await reload();
      await loadSeatInfo();
    } catch (err) {
      alert(err.message || 'Failed to remove member');
    } finally {
      setRemoveLoading(false);
      setShowRemovalModal(false);
      setUserToRemove(null);
    }
  };

  function SeatConfirmModal({ data, onConfirm, onCancel, loading }) {
    const cycleLabel = data.billingCycle === 'annual' ? 'yr' : 'mo';
    return (
      <div style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 2000, padding: '1rem',
      }}>
        <div style={{
          background: '#fff', borderRadius: 14, padding: '2rem',
          maxWidth: 420, width: '100%', fontFamily: 'Raleway, sans-serif',
          boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
        }}>
          <h3 style={{ margin: '0 0 0.5rem', fontSize: '1.05rem', fontWeight: 800, color: '#0C2D48' }}>
            Add a seat
          </h3>
          <p style={{ margin: '0 0 1rem', fontSize: '0.88rem', color: '#555', lineHeight: 1.6 }}>
            All seats are filled. Adding <strong>{data.userData.name || data.userData.email}</strong> requires 1 additional seat.
          </p>
          {data.priceInfo && (
            <div style={{
              background: '#f8f9fb', border: '1px solid #e0e0e0', borderRadius: 10,
              padding: '1rem', marginBottom: '1rem', fontSize: '0.85rem', lineHeight: 1.8,
            }}>
              <div>Current: <strong>{data.currentSeats} seats</strong></div>
              <div>After: <strong>{data.currentSeats + 1} seats</strong> — {fmtCurrency(data.priceInfo.amount * (data.currentSeats + 1), data.priceInfo.currency)}/{cycleLabel}</div>
            </div>
          )}
          <p style={{ margin: '0 0 1.5rem', fontSize: '0.82rem', color: '#777', lineHeight: 1.6 }}>
            A prorated charge applies at your next billing date.
          </p>
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
            <button
              onClick={onCancel}
              disabled={loading}
              style={{
                padding: '0.6rem 1.2rem', border: '1px solid #e0e0e0', borderRadius: 8,
                background: '#f8f9fb', color: '#0C2D48', fontWeight: 700,
                fontSize: '0.85rem', cursor: 'pointer', fontFamily: 'Raleway, sans-serif',
              }}
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={loading}
              style={{
                padding: '0.6rem 1.2rem', border: 'none', borderRadius: 8,
                background: '#0C2D48', color: '#fff', fontWeight: 700,
                fontSize: '0.85rem', cursor: loading ? 'not-allowed' : 'pointer',
                fontFamily: 'Raleway, sans-serif', opacity: loading ? 0.7 : 1,
              }}
            >
              {loading ? 'Adding…' : 'Add seat & member'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const UserCard = ({ user }) => {
    const openCount = teamTasks.filter(
      t => (t.userId === user.id || (t.assignedTeam || []).includes(user.id)) && !t.completed
    ).length;
    const deptColor = getDeptColor(user.department);

    return (
      <div className="user-card" style={{ borderLeft: `4px solid ${user.color || '#ccc'}` }}>
        <div className="user-avatar" style={{ backgroundColor: user.color || '#ccc' }}>
          {user.name.charAt(0).toUpperCase()}
        </div>

        <div className="user-info" style={{ flex: 1, minWidth: 0 }}>
          <h4 style={{ margin: '0 0 0.15rem', display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
            {user.name}
            {user.id === currentUser.id && <span className="current-user-badge">You</span>}
          </h4>

          {user.jobTitle && (
            <div style={{ fontSize: '0.8rem', color: '#555', fontWeight: 600, marginBottom: '0.2rem' }}>
              {user.jobTitle}
            </div>
          )}

          <p style={{ margin: '0 0 0.4rem', fontSize: '0.82rem', color: '#888' }}>{user.email}</p>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', alignItems: 'center' }}>
            <span className={`role-badge ${user.role}`}>{user.role.replace('_', ' ')}</span>

            {user.department && (
              <span style={{
                fontSize: '0.72rem', fontWeight: 700, padding: '0.2rem 0.55rem',
                borderRadius: 12, background: deptColor, color: getTextColor(deptColor),
                border: 'none',
              }}>
                {user.department}
              </span>
            )}

            {openCount > 0 && (
              <span style={{ fontSize: '0.72rem', color: '#666', background: '#f3f4f6', padding: '0.2rem 0.5rem', borderRadius: 12, border: '1px solid #e5e7eb' }}>
                {openCount} task{openCount !== 1 ? 's' : ''}
              </span>
            )}
          </div>
        </div>

        {isAdmin && user.id !== currentUser.id && (
          <div className="user-actions">
            <button onClick={() => setEditingUser({ ...user, role: user.teamRole || user.role })} className="btn-edit">Edit</button>
            <button onClick={() => handleDeleteUser(user.id)} className="btn-delete">Remove</button>
          </div>
        )}
      </div>
    );
  };

  // Build full department list including any custom ones from existing users
  const allKnownDepts = [...new Set([...DEPARTMENTS, ...customDepts, ...users.map(u => u.department).filter(Boolean)])];

  // Group stats by department
  const deptStats = allDepts.map(dept => ({
    dept,
    count: users.filter(u => u.department === dept).length,
    color: getDeptColor(dept),
  }));

  return (
    <div className="team-management">
      {/* Header */}
      <div className="team-header">
        <div>
          <h2 style={{ margin: 0 }}>Team Management</h2>
          <div className="team-stats" style={{ marginTop: '0.25rem' }}>
            {users.length} member{users.length !== 1 ? 's' : ''}
            {' · '}
            {users.filter(u => u.role === 'admin' || u.role === 'super_admin').length} admin{users.filter(u => u.role === 'admin' || u.role === 'super_admin').length !== 1 ? 's' : ''}
            {deptStats.length > 0 && ` · ${deptStats.length} department${deptStats.length !== 1 ? 's' : ''}`}
          </div>
        </div>
      </div>

      {/* Department filter pills */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1rem', alignItems: 'center' }}>
        {deptStats.map(({ dept, count, color }) => (
          <span key={dept} style={{
            display: 'inline-flex', alignItems: 'stretch', borderRadius: 20, overflow: 'hidden',
            background: color, opacity: deptFilter && deptFilter !== dept ? 0.55 : 1,
            transition: 'opacity 0.15s',
          }}>
            <button
              onClick={() => setDeptFilter(deptFilter === dept ? '' : dept)}
              style={{
                padding: '0.35rem 0.5rem 0.35rem 0.85rem', border: 'none', background: 'transparent',
                color: getTextColor(color), fontWeight: 700, fontSize: '0.78rem',
                cursor: 'pointer', fontFamily: 'Raleway, sans-serif',
              }}
            >
              {dept} <span style={{ opacity: 0.8 }}>({count})</span>
            </button>
            {isAdmin && (
              <button
                onClick={() => openEditDept(dept)}
                title="Edit department"
                style={{
                  padding: '0.35rem 0.65rem 0.35rem 0.2rem', border: 'none', background: 'transparent',
                  color: getTextColor(color), cursor: 'pointer', fontSize: '0.72rem',
                  opacity: editingDept?.originalName === dept ? 1 : 0.6,
                  fontFamily: 'Raleway, sans-serif', lineHeight: 1,
                }}
              >
                ✐
              </button>
            )}
          </span>
        ))}
        {/* Custom-only dept pills (those not yet assigned to any user) */}
        {customDepts.filter(d => !deptStats.find(s => s.dept === d)).map(dept => {
          const color = getDeptColor(dept);
          return (
            <span key={dept} style={{
              display: 'inline-flex', alignItems: 'stretch', borderRadius: 20, overflow: 'hidden',
              background: color,
            }}>
              <button
                onClick={() => setDeptFilter(deptFilter === dept ? '' : dept)}
                style={{
                  padding: '0.35rem 0.5rem 0.35rem 0.85rem', border: 'none', background: 'transparent',
                  color: getTextColor(color), fontWeight: 700, fontSize: '0.78rem',
                  cursor: 'pointer', fontFamily: 'Raleway, sans-serif',
                }}
              >
                {dept}
              </button>
              {isAdmin && (
                <button
                  onClick={() => openEditDept(dept)}
                  title="Edit department"
                  style={{
                    padding: '0.35rem 0.4rem 0.35rem 0.2rem', border: 'none', background: 'transparent',
                    color: getTextColor(color), cursor: 'pointer', fontSize: '0.72rem',
                    opacity: 0.6, lineHeight: 1,
                  }}
                >
                  ✐
                </button>
              )}
              {isAdmin && (
                <button
                  onClick={() => removeCustomDept(dept)}
                  title="Remove department"
                  style={{
                    padding: '0.35rem 0.65rem 0.35rem 0.1rem', border: 'none', background: 'transparent',
                    color: getTextColor(color), cursor: 'pointer', fontSize: '0.85rem',
                    opacity: 0.65, lineHeight: 1,
                  }}
                >
                  ×
                </button>
              )}
            </span>
          );
        })}
        {deptFilter && (
          <button onClick={() => setDeptFilter('')} style={{
            padding: '0.35rem 0.75rem', borderRadius: 20, border: '1.5px solid #ddd',
            background: '#f3f4f6', color: '#888', fontWeight: 600, fontSize: '0.78rem',
            cursor: 'pointer', fontFamily: 'Raleway, sans-serif',
          }}>
            Clear filter ✕
          </button>
        )}
        {isAdmin && !showAddDeptForm && !editingDept && (
          <button onClick={() => setShowAddDeptForm(true)} style={{
            padding: '0.35rem 0.65rem', borderRadius: 20,
            border: '1px dashed #aaa', background: 'none',
            color: '#888', fontSize: '0.78rem', fontWeight: 600,
            cursor: 'pointer', fontFamily: 'Raleway, sans-serif',
          }}>
            + Add Department
          </button>
        )}
      </div>

      {/* Shared dept form — Add or Edit */}
      {(showAddDeptForm || editingDept) && isAdmin && (() => {
        const isEditing = !!editingDept;
        const name  = isEditing ? editingDept.name  : newDeptName;
        const color = isEditing ? editingDept.color : newDeptColor;
        const setName  = v => isEditing ? setEditingDept(d => ({ ...d, name: v }))  : setNewDeptName(v);
        const setColor = v => isEditing ? setEditingDept(d => ({ ...d, color: v })) : setNewDeptColor(v);
        const onSave   = isEditing ? saveEditDept : addCustomDept;
        const onCancel = isEditing
          ? () => setEditingDept(null)
          : () => { setShowAddDeptForm(false); setNewDeptName(''); setNewDeptColor(''); };
        const canSave  = name.trim() && color;

        return (
          <div style={{
            padding: '1rem 1.1rem', marginBottom: '1.25rem',
            background: '#f8f9fb', border: '1px solid #e0e3e8', borderRadius: 10,
          }}>
            <div style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 700, fontSize: '0.82rem', color: '#0C2D48', marginBottom: '0.75rem' }}>
              {isEditing ? `Edit — ${editingDept.originalName}` : 'Add Department'}
            </div>
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && canSave && onSave()}
              placeholder="Department name"
              style={{
                width: '100%', padding: '0.45rem 0.75rem', borderRadius: 7,
                border: '1.5px solid #c5cdd4', fontSize: '0.88rem',
                fontFamily: 'Raleway, sans-serif', boxSizing: 'border-box',
                marginBottom: '0.75rem', outline: 'none',
              }}
            />
            {/* Colour swatches */}
            <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '0.65rem', alignItems: 'center' }}>
              {PRESET_COLORS.map(c => (
                <button
                  key={c}
                  onClick={() => setColor(c)}
                  style={{
                    width: 28, height: 28, borderRadius: '50%', background: c,
                    border: color === c ? '3px solid #0C2D48' : '2px solid #fff',
                    outline: color === c ? '2px solid #0C2D48' : 'none',
                    cursor: 'pointer', padding: 0, flexShrink: 0,
                  }}
                  title={c}
                />
              ))}
              <label title="Custom colour" style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                <input
                  type="color"
                  value={!PRESET_COLORS.includes(color) && color ? color : '#9ca3af'}
                  onChange={e => setColor(e.target.value)}
                  style={{ width: 28, height: 28, borderRadius: '50%', border: '2px solid #e0e3e8', padding: 0, cursor: 'pointer' }}
                />
              </label>
            </div>
            {/* Live preview + action buttons */}
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
              {name.trim() && color && (
                <span style={{
                  padding: '0.3rem 0.75rem', borderRadius: 20, background: color,
                  color: getTextColor(color), fontSize: '0.78rem', fontWeight: 700,
                  fontFamily: 'Raleway, sans-serif',
                }}>
                  {name.trim()}
                </span>
              )}
              <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                {isEditing && customDepts.includes(editingDept.originalName) && (
                  <button
                    onClick={() => { removeCustomDept(editingDept.originalName); setEditingDept(null); }}
                    style={{
                      padding: '0.4rem 0.85rem', borderRadius: 7, border: '1.5px solid #fca5a5',
                      background: '#fff', color: '#dc2626', fontWeight: 600, fontSize: '0.83rem',
                      cursor: 'pointer', fontFamily: 'Raleway, sans-serif',
                    }}
                  >
                    Delete
                  </button>
                )}
                <button
                  onClick={onCancel}
                  style={{
                    padding: '0.4rem 0.9rem', borderRadius: 7, border: '1.5px solid #dde3ea',
                    background: '#fff', color: '#666', fontWeight: 600, fontSize: '0.83rem',
                    cursor: 'pointer', fontFamily: 'Raleway, sans-serif',
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={onSave}
                  disabled={!canSave}
                  style={{
                    padding: '0.4rem 1rem', borderRadius: 7, border: 'none',
                    background: canSave ? '#0C2D48' : '#c5cdd4',
                    color: '#fff', fontWeight: 700, fontSize: '0.83rem',
                    cursor: canSave ? 'pointer' : 'not-allowed',
                    fontFamily: 'Raleway, sans-serif',
                  }}
                >
                  {isEditing ? 'Save Changes' : 'Add Department'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}


      {/* Controls */}
      <div className="team-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search by name, email, department or job title…"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>
        {isAdmin && (
          <button onClick={() => { setIsAddingUser(true); setNewUser(EMPTY_USER); }} className="btn-add-user">
            + Add Team Member
          </button>
        )}
      </div>

      {/* Add Member Modal */}
      {isAddingUser && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 560 }}>
            <div className="modal-header">
              <h3>Add Team Member</h3>
              <button onClick={() => setIsAddingUser(false)} className="btn-close">×</button>
            </div>
            <form onSubmit={handleAddUser} className="user-form">
              <UserFormFields data={newUser} onChange={setNewUser} allKnownDepts={allKnownDepts} />
              <div className="form-actions">
                <button type="button" onClick={() => setIsAddingUser(false)} className="btn-cancel" disabled={addLoading}>Cancel</button>
                <button type="submit" className="btn-save" disabled={addLoading}>{addLoading ? 'Adding…' : 'Add Member'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Member Modal */}
      {editingUser && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 560 }}>
            <div className="modal-header">
              <h3>Edit — {editingUser.name}</h3>
              <button onClick={() => setEditingUser(null)} className="btn-close">×</button>
            </div>
            <form onSubmit={handleUpdateUser} className="user-form">
              <UserFormFields data={editingUser} onChange={setEditingUser} allKnownDepts={allKnownDepts} />
              <div className="form-actions">
                <button type="button" onClick={() => setEditingUser(null)} className="btn-cancel">Cancel</button>
                <button type="submit" className="btn-save">Save Changes</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Seat confirm modal */}
      {seatConfirm && (
        <SeatConfirmModal
          data={{
            userData: seatConfirm.userData,
            currentSeats: seatInfo?.paidSeats || 0,
            priceInfo: seatInfo?.priceInfo || null,
            billingCycle: seatInfo?.billingCycle,
          }}
          loading={addLoading}
          onConfirm={() => submitAddMember(seatConfirm.userData, true)}
          onCancel={() => setSeatConfirm(null)}
        />
      )}

      {/* Remove Confirmation Modal */}
      {showRemovalModal && userToRemove && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 500 }}>
            <div className="modal-header">
              <h3>Remove {userToRemove.name}</h3>
              <button onClick={() => { setShowRemovalModal(false); setUserToRemove(null); }} className="btn-close">×</button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: '1.5rem' }}>
                {userToRemove.name} will lose access immediately. Their tasks will remain unassigned.
              </p>
            </div>
            <div className="modal-footer" style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', paddingTop: '1rem', borderTop: '1px solid #e9ecef' }}>
              <button
                onClick={() => { setShowRemovalModal(false); setUserToRemove(null); }}
                disabled={removeLoading}
                className="btn-cancel"
                style={{ padding: '0.5rem 1rem', background: '#6c757d', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmRemoval}
                disabled={removeLoading}
                style={{ padding: '0.5rem 1rem', background: '#dc3545', color: '#fff', border: 'none', borderRadius: 6, cursor: removeLoading ? 'not-allowed' : 'pointer', fontWeight: 700, opacity: removeLoading ? 0.7 : 1 }}
              >
                {removeLoading ? 'Removing…' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Users Grid */}
      <div className="users-grid">
        {filtered.map(user => <UserCard key={user.id} user={user} />)}
      </div>

      {filtered.length === 0 && (
        <div className="no-results">
          <p>No team members match "{searchQuery || deptFilter}"</p>
        </div>
      )}

      {/* Department overview table */}
      {deptStats.length > 0 && (
        <div style={{ marginTop: '2rem' }}>
          <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#0C2D48', marginBottom: '0.75rem' }}>Departments</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '0.5rem' }}>
            {deptStats.map(({ dept, count, color }) => (
              <div key={dept} style={{ background: '#fff', border: `1px solid ${color}30`, borderLeft: `4px solid ${color}`, borderRadius: 8, padding: '0.75rem 1rem' }}>
                <div style={{ fontWeight: 700, fontSize: '0.88rem', color }}>{dept}</div>
                <div style={{ fontSize: '0.78rem', color: '#888', marginTop: '0.15rem' }}>
                  {users.filter(u => u.department === dept).map(u => u.name).join(', ')}
                </div>
                <div style={{ fontSize: '0.72rem', color, marginTop: '0.3rem', fontWeight: 700 }}>{count} member{count !== 1 ? 's' : ''}</div>
              </div>
            ))}
          </div>
        </div>
      )}

    </div>
  );
};

export default TeamManagement;
